import { push } from 'redux-first-history';
import { ProfileTypes } from '@sda/enums';
import { getSanitizedVanityUrl } from '@sda/utils/url';
import { vanityUrlSelector } from '@client/selectors/helpers/router';
import { upsertPublicAct } from '@client/slices/entities/acts.reducer';
import { upsertPublicVenue } from '@client/slices/entities/venues.reducer';
import type { ListenerEffectAPI } from '@reduxjs/toolkit';
import type { AppDispatch, RootState } from '@client/store';

export const checkVanityUrlAvailability = async (props: { objectType: ProfileTypes, vanityUrl: string }): Promise<string> => {
  const { objectType, vanityUrl = '' } = props;
  let endpoint = null;

  const firebase = window.Firebase;

  switch (objectType) {
    case ProfileTypes.ACT:
      endpoint = firebase.SDK.Acts;
      break;
    case ProfileTypes.VENUE:
      endpoint = firebase.SDK.Venues;
      break;
    default:
      throw new Error('incorrect type provided');
  }

  const vanityUrlLowercase = getSanitizedVanityUrl(vanityUrl).toLowerCase();
  const existing = await endpoint.getBy((query) => query.isEqualTo('vanityUrlLowercase', vanityUrlLowercase));

  if (!existing) {
    return vanityUrl;
  }

  let total = 1;

  const totalExisting = await endpoint.listBy((query) => [
    query.isGreaterThanOrEqualTo('vanityUrlLowercase', `${vanityUrlLowercase}-1`),
    query.isLessThanOrEqualTo('vanityUrlLowercase', `${vanityUrlLowercase}-${Number.MAX_SAFE_INTEGER}`),
  ]);

  if (totalExisting) {
    total += Object.keys(totalExisting).length;
  }

  return `${vanityUrl}-${total}`;
};

export const getIdFromVanityUrl = async (listenerApi: ListenerEffectAPI<RootState, AppDispatch>): Promise<string> => {
  const firebase = window.Firebase;

  const state = listenerApi.getState();
  const { dispatch } = listenerApi;

  const selector = vanityUrlSelector(state);
  const { profileType, vanityUrl: id } = selector.params;

  const endpoint = profileType === 'artists' ? firebase.SDK.Acts : firebase.SDK.Venues;
  const upserter = profileType === 'artists' ? upsertPublicAct : upsertPublicVenue;

  const idProfile = await endpoint.get(id);

  if (idProfile) {
    dispatch(upserter({ ...idProfile, publicFetched: true } as any));

    return idProfile.id;
  }

  const vanityUrlLowercase = id.toLowerCase();

  const urlProfile = await endpoint.getBy((query) => query.isEqualTo('vanityUrlLowercase', vanityUrlLowercase));

  if (urlProfile) {
    dispatch(upserter({ ...urlProfile, publicFetched: true } as any));

    return urlProfile.id;
  }

  const urlCacheProfile = await endpoint.getBy((query) => query.contains('vanityUrlCache', vanityUrlLowercase));

  if (urlCacheProfile) {
    dispatch(upserter({ ...urlCacheProfile, publicFetched: true } as any));

    return urlCacheProfile.id;
  }

  dispatch(push('/404'));

  return null;
};
